import React, { useEffect, useState } from 'react'
import { Dimensions, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'

import styles, { colors, SCREEN_BREAKPOINT_NAV } from '../../styles/global'
import { translate } from '../../../i18n/TranslationService'
import ProjectHelper, {
    checkIfSelectedAllProjects,
    checkIfSelectedProject,
} from '../../SettingsView/ProjectsSettings/ProjectHelper'
import { hideFloatPopup, hideWebSideBar, setSelectedSidebarTab, storeCurrentUser } from '../../../redux/actions'
import store from '../../../redux/store'
import { dismissAllPopups } from '../../../utils/HelperFunctions'
import {
    DV_TAB_ROOT_CHATS,
    DV_TAB_ROOT_CONTACTS,
    DV_TAB_ROOT_GOALS,
    DV_TAB_ROOT_NOTES,
    DV_TAB_ROOT_TASKS,
} from '../../../utils/TabNavigationConstants'
import { allGoals } from '../../AllSections/allSectionHelper'

/**
 * Below this viewport width the five section labels no longer fit on one line in German or
 * Spanish ("Aufgaben Ziele Notizen Kontakte Chats" needs ~372px at 16px + 10px padding, and a
 * 390px phone leaves the card ~336px). English only just fits, which is why it was never noticed.
 * The mobile layout above this width keeps the centred scroller; below it the tabs become a fixed
 * single row with 15px labels, 1px tab padding and 5px card padding per side, leaving room for all
 * three languages even in the narrower project navigation on a 320px phone.
 */
export const COMPACT_TABS_BREAKPOINT = 420

export default function MainSectionTabsHeader({
    showSectionToggle = false,
    renderSectionToggle = null,
    renderRightAccessory = null,
}) {
    const dispatch = useDispatch()
    const selectedSidebarTab = useSelector(state => state.selectedSidebarTab)
    const selectedProjectIndex = useSelector(state => state.selectedProjectIndex)
    const project = useSelector(state => state.loggedUserProjects[selectedProjectIndex])
    const realProjectIds = useSelector(state => state.loggedUser.realProjectIds)
    const isAnonymous = useSelector(state => state.loggedUser.isAnonymous)
    const getViewportWidth = () =>
        typeof window !== 'undefined' && typeof window.innerWidth === 'number'
            ? window.innerWidth
            : Dimensions.get('window').width
    const [viewportWidth, setViewportWidth] = useState(getViewportWidth())

    const useMobileLayout = viewportWidth < SCREEN_BREAKPOINT_NAV
    const useCompactMobileTabs = useMobileLayout && viewportWidth < COMPACT_TABS_BREAKPOINT
    const useCompactDesktopTabs = !useMobileLayout && viewportWidth < 1500
    const tabs = [
        { text: 'Tasks', value: DV_TAB_ROOT_TASKS },
        { text: 'Goals', value: DV_TAB_ROOT_GOALS },
        { text: 'Notes', value: DV_TAB_ROOT_NOTES },
        { text: 'Contacts', value: DV_TAB_ROOT_CONTACTS },
        { text: 'Chats', value: DV_TAB_ROOT_CHATS },
    ]

    const inAllProjects = checkIfSelectedAllProjects(selectedProjectIndex)
    const accessGranted = !isAnonymous && (inAllProjects || (project && realProjectIds.includes(project.id)))
    const sectionToggle = showSectionToggle && renderSectionToggle ? renderSectionToggle() : null
    const rightAccessory = renderRightAccessory ? renderRightAccessory() : null
    const showRightArea = !!sectionToggle || !!rightAccessory

    useEffect(() => {
        const onResize = () => setViewportWidth(getViewportWidth())

        if (typeof window !== 'undefined') {
            window.addEventListener('resize', onResize)
            return () => window.removeEventListener('resize', onResize)
        }

        const subscription = Dimensions.addEventListener('change', onResize)
        return () => {
            if (subscription && subscription.remove) subscription.remove()
        }
    }, [])

    const onPressSectionTab = sectionValue => {
        if (sectionValue === selectedSidebarTab || !accessGranted) return

        const { loggedUser } = store.getState()
        dismissAllPopups(true, true, true)

        const actionsToDispatch = [hideFloatPopup(), setSelectedSidebarTab(sectionValue)]
        if (useMobileLayout) actionsToDispatch.push(hideWebSideBar())

        const isGuide = ProjectHelper.checkIfProjectIsGuide(selectedProjectIndex)
        let newCurrentUser = loggedUser

        if (!isGuide && sectionValue === DV_TAB_ROOT_GOALS && checkIfSelectedProject(selectedProjectIndex)) {
            newCurrentUser = allGoals
        }

        actionsToDispatch.push(storeCurrentUser(newCurrentUser))
        dispatch(actionsToDispatch)
    }

    const renderMobileTabs = compact =>
        tabs.map(tab => {
            const selected = selectedSidebarTab === tab.value
            return (
                <TouchableOpacity
                    key={tab.value}
                    style={[
                        localStyles.tabButton,
                        localStyles.tabButtonMobile,
                        compact && localStyles.tabButtonCompact,
                        selected && localStyles.tabButtonSelected,
                    ]}
                    onPress={() => onPressSectionTab(tab.value)}
                    disabled={!accessGranted}
                    accessible={false}
                >
                    <Text
                        style={[
                            localStyles.tabText,
                            localStyles.tabTextMobile,
                            compact && localStyles.tabTextCompact,
                            selected && localStyles.tabTextSelected,
                        ]}
                        numberOfLines={1}
                    >
                        {translate(tab.text)}
                    </Text>
                </TouchableOpacity>
            )
        })

    return (
        <View style={localStyles.container}>
            {useMobileLayout ? (
                <View style={[localStyles.controlsRowMobile, useCompactMobileTabs && localStyles.controlsRowCompact]}>
                    {useCompactMobileTabs ? (
                        <View
                            style={[
                                localStyles.tabsContainer,
                                localStyles.tabsContainerCompact,
                                !accessGranted && localStyles.tabsContainerDisabled,
                            ]}
                        >
                            {renderMobileTabs(true)}
                        </View>
                    ) : (
                        <ScrollView
                            horizontal={true}
                            showsHorizontalScrollIndicator={false}
                            contentContainerStyle={[
                                localStyles.tabsContainer,
                                localStyles.tabsContainerMobile,
                                !accessGranted && localStyles.tabsContainerDisabled,
                            ]}
                        >
                            {renderMobileTabs(false)}
                        </ScrollView>
                    )}

                    {showRightArea && (
                        <View style={localStyles.mobileControlsContainer}>
                            {sectionToggle && <View style={localStyles.sectionToggleRowMobile}>{sectionToggle}</View>}
                            {rightAccessory && (
                                <View style={localStyles.rightAccessoryRowMobile}>{rightAccessory}</View>
                            )}
                        </View>
                    )}
                </View>
            ) : (
                <View style={[localStyles.controlsRow, showRightArea && localStyles.controlsRowWithRight]}>
                    <View style={localStyles.tabsRow}>
                        <View style={[localStyles.tabsContainer, !accessGranted && localStyles.tabsContainerDisabled]}>
                            {tabs.map(tab => {
                                const selected = selectedSidebarTab === tab.value
                                return (
                                    <TouchableOpacity
                                        key={tab.value}
                                        style={[
                                            localStyles.tabButton,
                                            useCompactDesktopTabs && localStyles.tabButtonCompactDesktop,
                                            selected && localStyles.tabButtonSelected,
                                        ]}
                                        onPress={() => onPressSectionTab(tab.value)}
                                        disabled={!accessGranted}
                                        accessible={false}
                                    >
                                        <Text style={[localStyles.tabText, selected && localStyles.tabTextSelected]}>
                                            {translate(tab.text)}
                                        </Text>
                                    </TouchableOpacity>
                                )
                            })}
                        </View>
                    </View>

                    {showRightArea && (
                        <View style={localStyles.rightArea}>
                            {sectionToggle && (
                                <View
                                    style={[
                                        localStyles.sectionToggleRow,
                                        useCompactDesktopTabs && localStyles.sectionToggleRowCompactDesktop,
                                    ]}
                                >
                                    {sectionToggle}
                                </View>
                            )}
                            {rightAccessory && <View style={localStyles.rightAccessoryRow}>{rightAccessory}</View>}
                        </View>
                    )}
                </View>
            )}
        </View>
    )
}

MainSectionTabsHeader.propTypes = {
    showSectionToggle: PropTypes.bool,
    renderSectionToggle: PropTypes.func,
    renderRightAccessory: PropTypes.func,
}

const localStyles = StyleSheet.create({
    container: {
        width: '100%',
        justifyContent: 'center',
        alignItems: 'stretch',
        paddingTop: 24,
        paddingBottom: 26,
    },
    controlsRow: {
        width: '100%',
        backgroundColor: colors.UtilityBlue100,
        borderRadius: 14,
        borderWidth: 1,
        borderColor: colors.UtilityBlue125,
        paddingHorizontal: 10,
        paddingVertical: 8,
        boxShadow: '0px 4px 10px rgba(4,20,47,0.08)',
        elevation: 2,
    },
    controlsRowWithRight: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    controlsRowMobile: {
        width: '100%',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'flex-start',
        backgroundColor: colors.UtilityBlue100,
        borderRadius: 14,
        borderWidth: 1,
        borderColor: colors.UtilityBlue125,
        paddingHorizontal: 10,
        paddingVertical: 8,
        boxShadow: '0px 4px 10px rgba(4,20,47,0.08)',
        elevation: 2,
    },
    controlsRowCompact: {
        paddingHorizontal: 5,
    },
    tabsRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-start',
        flex: 1,
        minWidth: 0,
    },
    tabsContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-start',
        paddingHorizontal: 0,
        paddingVertical: 0,
    },
    tabsContainerMobile: {
        paddingHorizontal: 0,
        minWidth: '100%',
        flexGrow: 1,
        justifyContent: 'center',
    },
    tabsContainerCompact: {
        width: '100%',
        justifyContent: 'space-between',
    },
    tabsContainerDisabled: {
        opacity: 0.6,
    },
    tabButton: {
        height: 36,
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 14,
        marginHorizontal: 2,
        borderBottomWidth: 3,
        borderBottomColor: 'transparent',
    },
    tabButtonCompactDesktop: {
        paddingHorizontal: 8,
        marginHorizontal: 0,
    },
    tabButtonMobile: {
        height: 38,
        paddingHorizontal: 10,
        marginHorizontal: 0,
    },
    tabButtonCompact: {
        paddingHorizontal: 1,
        flexShrink: 1,
        minWidth: 0,
    },
    tabButtonSelected: {
        borderBottomColor: colors.Primary100,
    },
    tabText: {
        ...styles.subtitle1,
        color: colors.Text02,
    },
    tabTextMobile: {
        ...styles.subtitle1,
    },
    tabTextCompact: {
        fontSize: 15,
        lineHeight: 22,
        letterSpacing: 0,
    },
    tabTextSelected: {
        color: colors.Text01,
        fontFamily: 'Roboto-Medium',
    },
    rightArea: {
        marginLeft: 12,
        flexDirection: 'row',
        alignItems: 'center',
    },
    sectionToggleRow: {
        marginRight: 10,
    },
    sectionToggleRowCompactDesktop: {
        marginRight: 6,
    },
    rightAccessoryRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    mobileControlsContainer: {
        alignItems: 'center',
        justifyContent: 'center',
    },
    sectionToggleRowMobile: {
        marginTop: 14,
        alignSelf: 'center',
    },
    rightAccessoryRowMobile: {
        marginTop: 12,
        alignSelf: 'center',
        flexDirection: 'row',
        alignItems: 'center',
    },
})
